import { PRACTICE_APPS } from "../lib/practiceApps.ts";

/**
 * What a device does when its seat in an app was CLOSED from another device
 * (client 2026-11-04, ported from the in-game app's sessionClosedPolicy): a
 * teammate — or the same coach elsewhere — pressed "Close Other Sessions" on
 * a full app's dashboard tile.
 *
 * Closing is not signing out. The device stays signed in and goes back to the
 * dashboard; only a coach can sign themselves out, on their own device. It
 * never throws work away: pending plays get a bounded upload attempt first,
 * and whatever is still on the device is kept and uploads from the dashboard
 * (GlobalPendingSync) — the session is alive, unlike a release's.
 */

/** sessionStorage: the dashboard tells the coach why the device came back. */
export const SESSION_CLOSED_NOTICE_KEY = "mf.session-closed";

/** The most a close waits for pending plays to upload before leaving (as a release does in-game). */
export const CLOSE_FLUSH_BUDGET_MS = 10_000;

/**
 * The app key a practice app runs under (x-app-selected, the selected-app
 * store) → the session name the API closes seats by.
 */
export const SESSION_NAME_BY_APP_KEY: Readonly<Record<string, string>> = {
  OL: "OL",
  QB: "QB",
  DefensiveBack: "DB",
  DefLineMan: "DEF",
  DL: "DL",
  DefensiveCoordinator: "DC",
  LineBacker: "LB",
  RunningBack: "RB",
  Safety: "S",
  ST: "ST",
  TightEnd: "TE",
  WideReceiver: "WR",
};

/** A session name → the app's id (its path, "lb"). */
const APP_ID_BY_SESSION_NAME: Readonly<Record<string, string>> = {
  OL: "ol",
  QB: "qb",
  DB: "db",
  DEF: "def",
  DL: "dl",
  DC: "dc",
  LB: "lb",
  RB: "rb",
  S: "s",
  ST: "st",
  TE: "te",
  WR: "wr",
};

/** What the coach calls the app ("LB" → "Linebacker"), as the sidebar and dashboard say it. */
export const closedAppTitle = (app: string): string =>
  PRACTICE_APPS.find((entry) => entry.id === APP_ID_BY_SESSION_NAME[app])?.name ?? app;

/** The app a `session:closed` names; null when it names none. */
export function closedApp(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const app = (payload as { app?: unknown }).app;
  return typeof app === "string" && app.length > 0 ? app : null;
}

/**
 * Is this close about the app this device is in right now? The seat named is
 * the one the server closed; a device that has since moved to another app,
 * or is on the dashboard, holds no seat there and stays put.
 */
export function isCloseForThisApp(payload: unknown, currentAppKey: string | null | undefined): boolean {
  const app = closedApp(payload);
  if (app === null || !currentAppKey) return false;
  return (SESSION_NAME_BY_APP_KEY[currentAppKey] ?? currentAppKey) === app;
}

/** On the dashboard, once the device is back there. */
export function closedNoticeOnDashboard(app: string): string {
  return `Your ${closedAppTitle(app)} session was closed from another device. You're still signed in, and any plays not uploaded yet will upload from here.`;
}
